import { defineNuxtPlugin, useState, useRequestEvent } from "#app";
import { setCookie, sendRedirect, parseCookies } from "h3";

// Add TypeScript declarations for React Native specific properties
declare global {
  interface Window {
    reactNativeNotification?: any;
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * BRUTALLY SIMPLE REACT NATIVE DETECTION
 *
 * This plugin uses Nuxt's built-in state management to ensure consistent detection
 * between server and client. It ONLY considers a client as React Native when the
 * x-go-client header is exactly 'ReactNative' in the original HTTP request.
 */

// Global state that will be hydrated from server to client automatically
interface ReactNativeState {
  isReactNative: boolean;
  debugMode: boolean;
  platform: string | null;
  appVersion: string | null;
  deviceId: string | null;
  authToken: string | null;
  headers: Record<string, string>;
  redirectMethod: string;
  disableAuthRedirects: boolean;
}

export default defineNuxtPlugin((nuxtApp) => {
  console.log("Loading BRUTALLY simple React Native detection plugin");

  // Create or access the shared state that persists from SSR to CSR
  const rnState = useState<ReactNativeState>("reactNative", () => ({
    isReactNative: false,
    debugMode: false, // This MUST be false by default
    platform: null,
    appVersion: null,
    deviceId: null,
    authToken: null,
    headers: {},
    redirectMethod: "url-scheme", // Default to URL scheme for redirects
    disableAuthRedirects: false, // Default to allowing auth redirects
  }));

  // Always clear localStorage to prevent persistence
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem("x-go-client");
    console.log("Cleared x-go-client from localStorage to prevent persistence");
  }

  // SERVER-SIDE DETECTION
  if (typeof window === "undefined" && nuxtApp.ssrContext?.event?.req) {
    const headers = nuxtApp.ssrContext.event.req.headers;
    const event = nuxtApp.ssrContext.event;
    const cookies = parseCookies(event);
    const url = new URL(
      event.node.req.url || "",
      `http://${headers.host || "localhost"}`
    );
    const queryParams = url.searchParams;

    // Check for query parameter 'reactNative=true' as a fallback for detection
    const hasReactNativeQueryParam = queryParams.get("reactNative") === "true";

    console.log("SSR: Raw request cookies:", JSON.stringify(cookies));
    console.log("SSR: Raw request headers:", JSON.stringify(headers));
    console.log("SSR: Raw request URL:", event.node.req.url);
    console.log("SSR: Has reactNative query param:", hasReactNativeQueryParam);

    // Check for x-go-siteactiveid in actual headers first
    let siteActiveId = headers["x-go-siteactiveid"];

    // If not found in headers, check in go-debug-headers cookie
    if (!siteActiveId && cookies["go-debug-headers"]) {
      try {
        const debugHeaders = JSON.parse(decodeURIComponent(cookies["go-debug-headers"]));
        if (debugHeaders && debugHeaders["x-go-siteactiveid"]) {
          siteActiveId = debugHeaders["x-go-siteactiveid"];
          console.log("SSR: Found X-GO-SITEACTIVEID in go-debug-headers cookie:", siteActiveId);
        }
      } catch (e) {
        console.error("SSR: Error parsing go-debug-headers:", e);
      }
    }

    console.log(
      "SSR: Raw request X-GO-SITEACTIVEID:",
      siteActiveId || "undefined"
    );

    if (headers) {
      // Check if x-go-siteactiveid header exists and is not null
      const currentPath = nuxtApp.ssrContext?.event.path;

      // Get the SITEACTIVEID header if it exists
      if (
        siteActiveId &&
        siteActiveId !== "null"
      ) {
        const headerSiteActiveId = String(siteActiveId);
        const cookieSiteActiveId = cookies?.SITEACTIVEID;

        // CRITICAL: ALWAYS set the cookie from the header for React Native apps
        // This ensures cookies and headers are in sync
        if (!cookieSiteActiveId || cookieSiteActiveId !== headerSiteActiveId) {
          if (cookieSiteActiveId) {
            console.log(
              "Updating SITEACTIVEID cookie - header value differs from cookie value:"
            );
            console.log("  Old cookie value:", cookieSiteActiveId);
            console.log("  New header value:", headerSiteActiveId);
          } else {
            console.log(
              "Setting SITEACTIVEID cookie from header value:",
              headerSiteActiveId
            );
          }

          // CRITICAL: Ensure SITEACTIVEID is available both in cookies and JavaScript
          // This makes it accessible to both server and client requests

          // 1. Direct header manipulation - most reliable approach
          if (!event.node.res.headersSent) {
            // Create a properly formatted cookie string (match session.get.ts format)
            // IMPORTANT: SameSite=Lax allows the cookie to be sent in cross-domain requests
            // HttpOnly=false allows JavaScript to access the cookie
            const cookieStr = `SITEACTIVEID=${headerSiteActiveId}; Path=/; Max-Age=${60 * 60}; SameSite=Lax; HttpOnly=false`;

            // Get any existing set-cookie headers
            const existingCookies = event.node.res.getHeader("Set-Cookie");

            if (!existingCookies) {
              // No existing cookies, set our cookie as the only one
              event.node.res.setHeader("Set-Cookie", cookieStr);
            } else if (Array.isArray(existingCookies)) {
              // Existing cookies array, add our cookie to it
              event.node.res.setHeader("Set-Cookie", [
                ...existingCookies,
                cookieStr,
              ]);
            } else {
              // Single existing cookie string, make an array with both
              event.node.res.setHeader("Set-Cookie", [
                existingCookies.toString(),
                cookieStr,
              ]);
            }

            // Also set it in the go-debug-headers cookie to ensure consistent header access
            const debugHeadersStr = `go-debug-headers=${encodeURIComponent(JSON.stringify({ "x-go-siteactiveid": headerSiteActiveId }))}; Path=/; Max-Age=${60 * 60}; SameSite=Lax; HttpOnly=false`;
            if (Array.isArray(event.node.res.getHeader("Set-Cookie"))) {
              // @ts-ignore - TypeScript doesn't know getHeader can return string[]
              event.node.res.setHeader("Set-Cookie", [
                ...(event.node.res.getHeader("Set-Cookie") as string[]),
                debugHeadersStr,
              ]);
            } else if (event.node.res.getHeader("Set-Cookie")) {
              event.node.res.setHeader("Set-Cookie", [
                event.node.res.getHeader("Set-Cookie")?.toString() || "",
                debugHeadersStr,
              ]);
            } else {
              event.node.res.setHeader("Set-Cookie", debugHeadersStr);
            }
          }

          // 2. Also use the standard setCookie method as a backup
          setCookie(event, "SITEACTIVEID", headerSiteActiveId, {
            path: "/",
            httpOnly: false, // Must be false to allow client JavaScript to access it
            secure: process.env.NODE_ENV === "production",
            sameSite: "lax",
            maxAge: 60 * 60, // 60 minutes
          });

          // Store in context for this request
          event.context.siteActiveId = headerSiteActiveId;
        } else {
          // Cookie already exists and matches the header value
          console.log("SITEACTIVEID cookie already matches header value");
          event.context.siteActiveId = headerSiteActiveId;
        }
      }

      // IMPORTANT: Store empty object by default, then only add headers that ACTUALLY exist
      const extractedHeaders: Record<string, string> = {};

      // CRITICAL: Check for debug mode - ONLY enable if explicitly set to 'true'
      const debugMode = headers["x-go-debugmode"] === "true";
      rnState.value.debugMode = debugMode;

      // Only add headers that actually exist with non-empty values
      const headersList = [
        "x-go-debugmode",
        "x-go-client",
        "x-go-device-platform",
        "x-go-app-version",
        "x-go-device-id",
      ];

      // Log debug status
      console.log("Debug mode header value:", headers["x-go-debugmode"]);
      console.log("Setting debug mode to:", debugMode);

      // Only add headers that actually exist
      for (const header of headersList) {
        const value = headers[header] as string;
        if (value && value.trim() !== "") {
          extractedHeaders[header] = value;
        }
      }

      // Store only headers that actually exist
      rnState.value.headers = extractedHeaders;

      // Real React Native detection happens here - check for the custom header or query param
      // TESTING: Force desktop flow - disable React Native detection
      const forceDesktopFlow = true; // Testing flag
      if (forceDesktopFlow) {
        console.log("🧪 TESTING: React Native detection disabled in plugin - forcing desktop flow");
        rnState.value.isReactNative = false;
      } else if (
        headers["x-go-client"] === "PrvidrGoApp" ||
        hasReactNativeQueryParam
      ) {
        // We've detected a React Native client
        if (headers["x-go-client"] === "PrvidrGoApp") {
          console.log(
            "SSR: Detected React Native client from x-go-client header"
          );
        } else if (hasReactNativeQueryParam) {
          console.log(
            "SSR: Detected React Native client from reactNative=true query parameter"
          );
        }
        rnState.value.isReactNative = true;

        // Extract platform if available
        if (headers["x-go-platform"]) {
          rnState.value.platform = String(headers["x-go-platform"]);
          console.log(`SSR: React Native platform: ${rnState.value.platform}`);
        } else if (headers["x-go-device-platform"]) {
          rnState.value.platform = String(headers["x-go-device-platform"]);
          console.log(`SSR: React Native platform: ${rnState.value.platform}`);
        }

        // Extract app version if available
        if (headers["x-go-app-version"]) {
          rnState.value.appVersion = String(headers["x-go-app-version"]);
          console.log(
            `SSR: React Native app version: ${rnState.value.appVersion}`
          );
        }

        // Extract device ID if available
        if (headers["x-go-device-id"]) {
          rnState.value.deviceId = String(headers["x-go-device-id"]);
          console.log(`SSR: React Native device ID: ${rnState.value.deviceId}`);
        }

        // Extract auth token if available
        if (headers["x-go-auth-token"]) {
          rnState.value.authToken = String(headers["x-go-auth-token"]);
          console.log(
            `SSR: React Native auth token: ${rnState.value.authToken}`
          );
        }
      }

      console.log(
        `SSR: React Native detection = ${rnState.value.isReactNative ? "YES ✓" : "NO ✗"}`
      );
    }
  }

  // CLIENT-SIDE DETECTION - maintain consistency with server detection
  if (typeof window !== "undefined") {
    console.log("CSR: Running React Native client-side detection");
    console.log(
      "CSR: isReactNative state from SSR:",
      rnState.value.isReactNative
    );

    // Check for the reactNative=true query parameter on the client side
    const urlParams = new URLSearchParams(window.location.search);
    const hasReactNativeQueryParam = urlParams.get("reactNative") === "true";

    if (hasReactNativeQueryParam && !rnState.value.isReactNative) {
      console.log(
        "CSR: Detected reactNative=true query parameter - setting isReactNative"
      );
      rnState.value.isReactNative = true;
    }

    // This will NOT override the server-side detection
    // The only client-side detection we do is checking if the ReactNativeWebView global is present
    // This helps in case someone manually accesses the site in a WebView
    if (
      typeof window.ReactNativeWebView !== "undefined" &&
      !rnState.value.isReactNative
    ) {
      console.log(
        "CSR: Detected ReactNativeWebView object in window - setting isReactNative"
      );
      rnState.value.isReactNative = true;
    }

    console.log(
      `CSR: React Native from hydrated state = ${rnState.value.isReactNative ? "YES ✓" : "NO ✗"}`
    );

    // CRITICAL: For React Native apps, set up postMessage communication
    if (rnState.value.isReactNative) {
      // Function to send notification to React Native via postMessage
      const sendNotificationToReactNative = () => {
        try {
          // Check if we have a notification object set by the auth flow
          if (window.reactNativeNotification) {
            console.log(
              "[GO-POSTMESSAGE] Sending notification to React Native WebView:",
              window.reactNativeNotification
            );

            // Use postMessage to communicate with the React Native WebView
            if (
              window.ReactNativeWebView &&
              typeof window.ReactNativeWebView.postMessage === "function"
            ) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  type: "GO_NOTIFICATION",
                  payload: window.reactNativeNotification,
                })
              );
              console.log(
                "[GO-POSTMESSAGE] Successfully sent notification via postMessage"
              );
            } else {
              console.warn(
                "[GO-POSTMESSAGE] ReactNativeWebView not available for postMessage"
              );
            }

            // Also look for notification in DOM as fallback
            const notificationElement = document.getElementById(
              "react-native-notification"
            );
            if (
              notificationElement &&
              notificationElement.getAttribute("data-notification")
            ) {
              try {
                const notificationStr =
                  notificationElement.getAttribute("data-notification");
                if (notificationStr) {
                  const domNotification = JSON.parse(notificationStr);
                  console.log(
                    "[GO-POSTMESSAGE] Found notification in DOM:",
                    domNotification
                  );

                  // Send it if we haven't already sent the same notification
                  if (
                    !window.reactNativeNotification ||
                    JSON.stringify(domNotification) !==
                      JSON.stringify(window.reactNativeNotification)
                  ) {
                    if (
                      window.ReactNativeWebView &&
                      typeof window.ReactNativeWebView.postMessage ===
                        "function"
                    ) {
                      window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                          type: "GO_NOTIFICATION",
                          payload: domNotification,
                        })
                      );
                      console.log(
                        "[GO-POSTMESSAGE] Successfully sent DOM notification via postMessage"
                      );
                    }
                  }
                }
              } catch (e) {
                console.error(
                  "[GO-POSTMESSAGE] Error parsing DOM notification:",
                  e
                );
              }
            }
          }
        } catch (e) {
          console.error(
            "[GO-POSTMESSAGE] Error sending notification to React Native:",
            e
          );
        }
      };

      // Send notification immediately if document is already loaded
      if (document.readyState === "complete") {
        sendNotificationToReactNative();
      } else {
        // Otherwise wait for page to load
        window.addEventListener("load", sendNotificationToReactNative);
      }

      // Also set up a MutationObserver to detect dynamically added notifications
      if ("MutationObserver" in window) {
        const observer = new MutationObserver((mutations) => {
          for (const mutation of mutations) {
            if (mutation.type === "childList" && mutation.addedNodes.length) {
              // Check for notification element
              const hasNotification = Array.from(mutation.addedNodes).some(
                (node) => {
                  if (node.nodeType === Node.ELEMENT_NODE) {
                    const elem = node as Element;
                    return (
                      elem.id === "react-native-notification" ||
                      !!elem.querySelector("#react-native-notification")
                    );
                  }
                  return false;
                }
              );

              if (hasNotification) {
                sendNotificationToReactNative();
              }
            }
          }
        });

        // Start observing the document body for changes
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

  // Create the context object to provide to the application
  const reactNativeContext = {
    get isApp() {
      return rnState.value.isReactNative;
    },
    get platform() {
      return rnState.value.platform;
    },
    get appVersion() {
      return rnState.value.appVersion;
    },
    get deviceId() {
      return rnState.value.deviceId;
    },
    get authToken() {
      return rnState.value.authToken;
    },
    get debugMode() {
      return rnState.value.debugMode;
    },
    get allHeaders() {
      return rnState.value.headers;
    },
    get redirectMethod() {
      return rnState.value.redirectMethod;
    },
    // Get custom headers needed for WebView requests
    get customHeaders() {
      if (rnState.value.isReactNative) {
        return {
          "X-GO-React-Native-Client": "true",
          "X-GO-Redirect-Method": rnState.value.redirectMethod,
        };
      }
      return {};
    },
    // Simplified helper methods
    hasCapability: (capability: string) => false,
    isVersionAtLeast: (version: string) => false,
  };

  // For debugging - check what we're providing
  console.log(
    `Plugin providing reactNative.isApp = ${reactNativeContext.isApp}`
  );

  // Provide to the application
  return {
    provide: {
      reactNative: reactNativeContext,
    },
  };
});
